import i18n from 'i18next';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {TOOLTIP_DELAY} from '../../../constants';
import {SORT} from '../../../incase-domain/common/sort.enum';
import Button from '../../../incase-domain/components/button/Button';
import {IPresentation, IPresentationMeta} from '../../../incase-domain/presentation';
import {IPaginatedResult} from '../../../incase-domain/search';
import {selectPresentationToEditAction} from '../../../store/actions/presentation';
import {toggleViewAction} from '../../../store/actions/view';
import {viewSelector} from '../../../store/selectors/view';
import {prettyDateFormat} from '../../../utils/dateTimeUtils';
import {isPresentationRunnable} from '../../../utils/presentation';
import DataView, {ViewMode} from '../common/data/DataView';
import {createDataViewListStateProps, ListParams, SetListParamsOptions} from '../../../hooks/listParams';
import TooltipWrapper from '../common/TooltipWrapper';
import PresentationWizard from '../presentationWizard/PresentationWizard';
import ProtectionTypeIcons from '../ProtectionTypeIcons';
import PresentationListButtons, {BUTTON_OPTIONS} from './PresentationListButtons';

interface SharedPresentationListViewProps {
    presentationList: IPaginatedResult<IPresentation>;
    listParams: ListParams;
    setListParams: (partial: Partial<ListParams>, options?: SetListParamsOptions) => void;
    setPresentationToDelete: React.Dispatch<React.SetStateAction<IPresentation[]>>;
    setDeletePresentations: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedPresentationForLinks: React.Dispatch<React.SetStateAction<IPresentation | undefined>>;
    loading?: boolean;
}

export default function SharedPresentationListView(props: SharedPresentationListViewProps): JSX.Element {
    const {
        presentationList,
        listParams,
        setListParams,
        setPresentationToDelete,
        setDeletePresentations,
        setSelectedPresentationForLinks,
        loading,
    } = props;

    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const viewMode = useSelector(viewSelector);

    const [presentationWizard, setPresentationWizard] = useState(false);
    const [selectedPresentation, setSelectedPresentation] = useState<IPresentationMeta | null>(null);

    const renderActions = (presentation: IPresentation): JSX.Element => {
        return (
            <PresentationListButtons
                presentation={presentation}
                setDeletePresentations={setDeletePresentations}
                setPresentationToDelete={setPresentationToDelete}
                onCreatePresentationLinks={setSelectedPresentationForLinks}
                buttonOptions={[
                    BUTTON_OPTIONS.COPY_PRESENTATION,
                    BUTTON_OPTIONS.DIVIDER,
                    BUTTON_OPTIONS.DOWNLOAD_PRESENTATION,
                    BUTTON_OPTIONS.CREATE_PRESENTATION_LINKS,
                    BUTTON_OPTIONS.PRESENTATION_REPORTS,
                    BUTTON_OPTIONS.DIVIDER,
                    BUTTON_OPTIONS.DELETE_PRESENTATION,
                ]}
            />
        );
    };

    const renderName = (presentation: IPresentation): JSX.Element => {
        return (
            <>
                {presentation.name}
                {presentation.isShared && (
                    <TooltipWrapper id="listView-isShared" content={t('editorPage.isShared')} place="left" delayShow={TOOLTIP_DELAY}>
                        <Button
                            leading="solar:users-group-rounded-bold"
                            size="sm"
                            variant="ghost"
                            square
                            onClick={(e) => e?.stopPropagation()}
                        />
                    </TooltipWrapper>
                )}
            </>
        );
    };

    const renderOwner = (presentation: IPresentation): JSX.Element => {
        return <>{presentation.userEmail}</>;
    };

    const renderLastModified = (presentation: IPresentation): JSX.Element => {
        return <>{prettyDateFormat(new Date(presentation.lastModified), i18n.language)}</>;
    };

    const renderProtection = (presentation: IPresentation): JSX.Element => {
        return <ProtectionTypeIcons protectionTypes={presentation.protectionTypes || []} />;
    };

    const renderPlay = (presentation: IPresentation): JSX.Element => {
        return (
            <Button
                className="e-table__actions_button"
                dataCy="playPresentationButton"
                size="2xs"
                leading="solar:play-bold"
                color="secondary"
                variant="solid"
                onClick={(e) => {
                    e?.stopPropagation();
                    setPresentationWizard(true);
                    setSelectedPresentation(presentation);
                }}
            >
                {t('editorPage.play')}
            </Button>
        );
    };

    return (
        <>
            <div className="c-presentationName">
                <DataView
                    initialView={viewMode}
                    onViewChange={(view) => {
                        dispatch(toggleViewAction(view));
                    }}
                    views={[
                        {
                            columns: [
                                {
                                    label: t('editorPage.presentationTitle'),
                                    key: 'name',
                                    direction: listParams.sortBy?.name || SORT.DESC,
                                    sortable: true,
                                    body: (row) => renderName(row),
                                },
                                {
                                    label: t('editorPage.sharePresentations.presentationOwner'),
                                    key: 'userEmail',
                                    direction: listParams.sortBy?.userEmail || SORT.DESC,
                                    sortable: true,
                                    body: (row) => renderOwner(row),
                                },
                                {
                                    label: t('editorPage.lastUpdate'),
                                    key: 'lastModified',
                                    direction: listParams.sortBy?.lastModified || SORT.DESC,
                                    sortable: true,
                                    body: (row) => renderLastModified(row),
                                    width: '11rem',
                                },
                                {
                                    label: t('editorPage.protection'),
                                    body: (row) => renderProtection(row),
                                    width: '5rem',
                                },
                                {
                                    body: (row) => renderPlay(row),
                                    width: '5rem',
                                },
                                {
                                    body: (row) => renderActions(row),
                                    width: '4rem',
                                },
                            ],
                            view: ViewMode.TABLE,
                        },
                        {
                            view: ViewMode.GRID,
                            columnsGap: '1rem',
                            rowsGap: '1rem',
                            renderItem: (row, isLoading, onSelectItem, isSelected) => {
                                return (
                                    <div className={`c-presentationItem ${isSelected ? 'c-presentationItem--selected' : ''}`}>
                                        <div className="c-presentationItem__imageWrapper" onClick={() => onSelectItem?.(row)}>
                                            {row.firstSlidePreview ? (
                                                <img src={row.firstSlidePreview} alt={row.name} className="c-presentationItem__image" />
                                            ) : (
                                                <div className="c-presentationItem__placeholder">
                                                    <Button
                                                        leading={isLoading ? 'svg-spinners:eclipse' : 'hugeicons:image-not-found-02'}
                                                        size="2xl"
                                                        variant="ghost"
                                                        disablePointerEvents
                                                        padded={false}
                                                    />
                                                    <div className="c-presentationItem__placeholder_text">
                                                        {isLoading ? 'Loading...' : 'No Preview Available'}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="c-presentationItem__buttons">
                                                <Button
                                                    size="2xl"
                                                    variant="ghost"
                                                    leading="solar:play-bold"
                                                    color="frost"
                                                    dataCy="playPresentationButton"
                                                    disableHover
                                                    disabled={!isPresentationRunnable(row.protectionTypes)}
                                                    onClick={(e) => {
                                                        e?.stopPropagation();
                                                        setPresentationWizard(true);
                                                        setSelectedPresentation(row);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="c-presentationItem__body">
                                            <div className="c-presentationItem__meta">
                                                <div className="u-flex u-gap-1 u-v-center">
                                                    <div className="c-presentationItem__title">{row.name}</div>
                                                </div>
                                                <div className="c-presentationItem__lastUpdated">
                                                    {row.userEmail}
                                                    {' · '}
                                                    {prettyDateFormat(new Date(row.lastModified), i18n.language)}
                                                </div>
                                            </div>
                                            <div className="c-presentationItem__buttons">
                                                <Button
                                                    color="frost"
                                                    variant="ghost"
                                                    size="xs"
                                                    leading="solar:play-linear"
                                                    onClick={(e) => {
                                                        e?.stopPropagation();
                                                        setPresentationWizard(true);
                                                        setSelectedPresentation(row);
                                                    }}
                                                >
                                                    {t('editorPage.play')}
                                                </Button>
                                                <Button
                                                    color="frost"
                                                    variant="ghost"
                                                    size="xs"
                                                    leading="solar:pen-2-linear"
                                                    onClick={() => {
                                                        if (row.isReadOnly) return;
                                                        dispatch(selectPresentationToEditAction(row._id, navigate));
                                                        navigate(`/presentations/${row._id}`);
                                                    }}
                                                >
                                                    {t('editorPage.edit')}
                                                </Button>
                                                <div style={{flexGrow: 1}} />
                                                <PresentationListButtons
                                                    presentation={row}
                                                    setDeletePresentations={setDeletePresentations}
                                                    setPresentationToDelete={setPresentationToDelete}
                                                    onCreatePresentationLinks={setSelectedPresentationForLinks}
                                                    buttonOptions={[
                                                        BUTTON_OPTIONS.COPY_PRESENTATION,
                                                        BUTTON_OPTIONS.DIVIDER,
                                                        BUTTON_OPTIONS.DOWNLOAD_PRESENTATION,
                                                        BUTTON_OPTIONS.CREATE_PRESENTATION_LINKS,
                                                        BUTTON_OPTIONS.PRESENTATION_REPORTS,
                                                        BUTTON_OPTIONS.DIVIDER,
                                                        BUTTON_OPTIONS.DELETE_PRESENTATION,
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            },
                        },
                    ]}
                    rows={presentationList.content}
                    rowIdentifier={(row) => row._id}
                    loading={loading}
                    onRowClick={(row) => {
                        if (row.isReadOnly) return;
                        dispatch(selectPresentationToEditAction(row._id, navigate));
                        navigate(`/presentations/${row._id}`);
                    }}
                    enableSearch
                    paginationData={{
                        pageSize: listParams.size,
                        currentPage: listParams.page,
                        itemsCount: presentationList.numberOfElements,
                    }}
                    {...dataViewListState}
                />
            </div>

            {selectedPresentation && (
                <PresentationWizard
                    presentation={selectedPresentation}
                    open={presentationWizard}
                    close={() => {
                        setPresentationWizard(false);
                        setSelectedPresentation(null);
                    }}
                    onPresentationUpdate={setSelectedPresentation}
                />
            )}
        </>
    );
}
